'use client';

import { JSX } from 'react';
import classNames from 'classnames';
import { SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import orPromotionalBlock from './or-promotional-block.variants';

// components
import { MlCard } from '@/components/molecules/ml-card/ml-card';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtCarousel } from '@/components/atoms/at-carousel/at-carousel';
import { MlCopyBlock } from '@/components/molecules/ml-copy-block/ml-copy-block';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';

// types
import { EThemes } from '@/lib/contentstack-sdk/types';
import { IParsedPromo } from '@/lib/contentstack-sdk/utils/parseHelpers';
import {
  EClickableTarget,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { IOrPromotionalBlockProps } from './or-promotional-block.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';

// hooks
import useBreakpoint from '@/hooks/useBreakpoint';

export const OrPromotionalBlock = ({
  autoplay,
  blockAlignment,
  cta,
  description,
  header,
  eyebrow,
  subhead,
  promos,
  theme,
  title,
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
}: IOrPromotionalBlockProps): JSX.Element => {
  const classes = orPromotionalBlock({ topSpacing, bottomSpacing });
  const breakpoint = useBreakpoint();
  const isMobile =
    breakpoint === 'md' || breakpoint === 'sm' || breakpoint === 'xs';
  const promoAlignment = isMobile ? 'center' : 'left';

  return (
    <div
      data-theme={theme}
      data-testid="promotional-block"
      className={classNames('bg-skin-secondary')}
    >
      <div className="container">
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
        <div className={classNames(classes, 'grid grid-cols-12 items-center')}>
          <div className="col-span-12 col-start-1 mb-16 text-center lg:col-span-4 lg:mb-0 lg:mr-14">
            <MlCopyBlock
              title={title}
              header={header}
              alignment={blockAlignment}
              eyebrow={eyebrow}
              subhead={subhead}
            >
              {description && (
                <AtText className="mb-8 text-skin-tertiary lg:mb-15">
                  {description}
                </AtText>
              )}
              <AtClickable
                variant={EClickableVariants.PRIMARY}
                outline
                interactLocation="or-promotional-block"
                label={cta.label}
                href={cta.url.href}
                target={
                  EClickableTarget[cta.target as keyof typeof EClickableTarget]
                }
              />
            </MlCopyBlock>
          </div>
          <div className="col-span-12 w-full lg:col-span-8">
            <AtCarousel
              autoplay={autoplay ? { delay: 5000 } : false}
              buttonPosition="below"
              buttonTheme={theme === 'dark' ? 'light' : 'dark'}
              containerClasses="mb-12 lg:mb-8"
              disableNav={promos?.length <= 1}
              modules={[Autoplay]}
              slidesPerView={1}
              spaceBetween={1}
            >
              {promos?.map((promo: IParsedPromo, i: number) => (
                <SwiperSlide key={i}>
                  <PromoBlockSlide alignment={promoAlignment} {...promo} />
                </SwiperSlide>
              ))}
            </AtCarousel>
          </div>
        </div>
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
      </div>
    </div>
  );
};

export interface IPromoBlockSlide extends IParsedPromo {
  alignment?: 'left' | 'center';
}

export const PromoBlockSlide = ({
  alignment,
  body,
  ctaProps,
  chipProps,
  image,
  title,
  variant,
}: IPromoBlockSlide) => {
  return (
    <div className="flex flex-col items-stretch lg:flex-row">
      {image && (
        <div className="relative mb-18 mr-6 aspect-video w-full lg:mb-0 lg:aspect-square lg:w-1/2">
          <AtMedia
            sizes="25vw"
            src={image.src}
            alt={image.alt}
            className="h-full w-full object-cover"
          />
        </div>
      )}
      <MlCard
        alignment={alignment}
        theme={EThemes.DARK}
        headerElement="h3"
        variant={variant}
        title={title}
        body={body}
        chipProps={chipProps}
        ctaProps={{ ...ctaProps, interactLocation: 'or-promotional-block' }}
        className="flex w-full flex-col"
      />
    </div>
  );
};

OrPromotionalBlock.PromoBlockSlide = PromoBlockSlide;
